import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Sparkle, ClockCounterClockwise, ArrowCounterClockwise } from '@phosphor-icons/react';
import { Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel } from './ui/dropdown-menu';
import { improveTextAI } from '../services/api';

const AI_ACTIONS = [
  { key: 'improve', label: 'Améliorer la rédaction' },
  { key: 'formal', label: 'Rendre formel' },
  { key: 'fix', label: "Corriger l'orthographe" },
  { key: 'shorten', label: 'Raccourcir' },
  { key: 'expand', label: 'Développer' },
];

const HISTORY_MAX = 3;
const historyKey = (field) => `ai-history:${(field || 'default').toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 60)}`;
const readHistory = (field) => {
  try { return JSON.parse(localStorage.getItem(historyKey(field)) || '[]'); } catch { return []; }
};
const pushHistory = (field, entry) => {
  try {
    const list = readHistory(field).filter((e) => e.text !== entry.text).slice(0, HISTORY_MAX - 1);
    list.unshift(entry);
    localStorage.setItem(historyKey(field), JSON.stringify(list));
  } catch { /* localStorage quota — ignoré */ }
};

/**
 * Bouton d'assistance IA pour un champ texte.
 * - `value` : contenu actuel du champ
 * - `field` : libellé du champ (contexte envoyé à l'IA + clé de l'historique local)
 * - `onApply(newText)` : appelé quand l'utilisateur clique « Remplacer »
 */
export function AIAssistButton({ value, field, onApply, testId }) {
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [customOpen, setCustomOpen] = useState(false);
  const [customInstruction, setCustomInstruction] = useState('');
  const [history, setHistory] = useState([]);

  useEffect(() => { setHistory(readHistory(field)); }, [field, preview]);

  const run = async (action, customInstr) => {
    if (!value || value.trim().length < 3) {
      toast.error("Saisissez d'abord un texte (min 3 caractères)");
      return;
    }
    if (action === 'custom' && (!customInstr || customInstr.trim().length < 3)) {
      toast.error('Décrivez la transformation souhaitée (min 3 caractères)');
      return;
    }
    setLoading(true);
    try {
      const r = await improveTextAI({ text: value, action, field, custom_instruction: customInstr });
      const actionLabel = action === 'custom'
        ? `Ton sur mesure — « ${customInstr.slice(0, 40)}${customInstr.length > 40 ? '…' : ''} »`
        : AI_ACTIONS.find((a) => a.key === action)?.label || action;
      pushHistory(field, { text: r.data.text, action, actionLabel, date: new Date().toISOString() });
      setPreview({ text: r.data.text, action, actionLabel, custom_instruction: customInstr });
      setCustomOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur de l'assistance IA");
    }
    setLoading(false);
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="inline-flex items-center gap-1 text-[11px] font-medium text-[#2D5A4C] hover:text-[#1E4136] hover:bg-[#2D5A4C]/10 px-2 py-0.5 rounded transition-colors disabled:opacity-50"
            disabled={loading}
            data-testid={testId}
            title="Assistance IA de rédaction"
          >
            {loading ? <Loader2 size={12} className="animate-spin" /> : <Sparkle size={12} weight="fill" />}
            IA
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" data-testid={`${testId}-menu`}>
          {AI_ACTIONS.map((a) => (
            <DropdownMenuItem key={a.key} onClick={() => run(a.key)} data-testid={`${testId}-${a.key}`}>
              {a.label}
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => { setCustomInstruction(''); setCustomOpen(true); }} data-testid={`${testId}-custom`}>
            Ton sur mesure…
          </DropdownMenuItem>
          {history.length > 0 && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-[#5E6D66] flex items-center gap-1">
                <ClockCounterClockwise size={11} /> 3 dernières propositions
              </DropdownMenuLabel>
              {history.map((h, i) => (
                <DropdownMenuItem
                  key={i}
                  className="flex-col items-start gap-0.5"
                  onClick={() => { onApply(h.text); toast.success('Proposition restaurée'); }}
                  data-testid={`${testId}-history-${i}`}
                >
                  <span className="text-[10px] text-[#2D5A4C] font-semibold uppercase tracking-wider truncate max-w-[240px]">
                    <ArrowCounterClockwise size={10} className="inline mr-1" /> {h.actionLabel}
                  </span>
                  <span className="text-xs text-[#1A2421] truncate max-w-[240px]">{h.text}</span>
                </DropdownMenuItem>
              ))}
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {customOpen && (
        <Dialog open onOpenChange={(o) => !o && setCustomOpen(false)}>
          <DialogContent className="max-w-md" data-testid={`${testId}-custom-dialog`}>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-[#1A2421]">
                <Sparkle size={18} className="text-[#2D5A4C]" weight="fill" /> Ton sur mesure
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-2">
              <p className="text-xs text-[#5E6D66]">
                Décrivez la transformation souhaitée. Ex : « plus percutant », « pour une collectivité », « avec un ton bienveillant », « ajouter les enjeux ESS ».
              </p>
              <Input
                autoFocus
                value={customInstruction}
                onChange={(e) => setCustomInstruction(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') run('custom', customInstruction); }}
                placeholder="Votre instruction…"
                maxLength={500}
                data-testid={`${testId}-custom-input`}
              />
            </div>
            <DialogFooter className="gap-2">
              <Button variant="ghost" onClick={() => setCustomOpen(false)} data-testid={`${testId}-custom-cancel`}>Annuler</Button>
              <Button
                className="bg-[#2D5A4C] hover:bg-[#1E4136]"
                disabled={loading || customInstruction.trim().length < 3}
                onClick={() => run('custom', customInstruction)}
                data-testid={`${testId}-custom-run`}
              >
                {loading ? <Loader2 size={14} className="animate-spin mr-1" /> : null}Générer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {preview && (
        <Dialog open onOpenChange={(o) => !o && setPreview(null)}>
          <DialogContent className="max-w-lg" data-testid={`${testId}-preview`}>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-[#1A2421]">
                <Sparkle size={18} className="text-[#2D5A4C]" weight="fill" /> Proposition IA — {preview.actionLabel}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <p className="text-[10px] uppercase text-[#5E6D66] font-semibold mb-1">Texte actuel</p>
                <p className="text-sm text-[#5E6D66] bg-[#F5F4EF] rounded p-2 whitespace-pre-wrap">{value}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase text-[#2D5A4C] font-semibold mb-1">Proposition</p>
                <p className="text-sm text-[#1A2421] bg-emerald-50 border border-emerald-200 rounded p-2 whitespace-pre-wrap" data-testid={`${testId}-suggestion`}>
                  {preview.text}
                </p>
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="ghost" onClick={() => setPreview(null)} data-testid={`${testId}-cancel`}>Annuler</Button>
              <Button variant="outline" disabled={loading} onClick={() => run(preview.action, preview.custom_instruction)} data-testid={`${testId}-retry`}>
                {loading ? <Loader2 size={14} className="animate-spin mr-1" /> : null}Réessayer
              </Button>
              <Button
                className="bg-[#2D5A4C] hover:bg-[#1E4136]"
                onClick={() => { onApply(preview.text); setPreview(null); toast.success('Texte remplacé'); }}
                data-testid={`${testId}-apply`}
              >
                Remplacer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
